// @ts-nocheck
'use client'

import ActivityLoading from '@/components/activity-loading'
import useFeedBlips from '@/utils/hooks/useFeedBlips'

import React, { ReactElement, useState, useEffect, useRef } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { formatActivityBtc } from '@/utils/format-activity-btc'
import { formatActivityUsd } from '@/utils/format-activity-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import { MarketplaceIcon, saleMarketplace } from '@/components/marketplace-icon'
import InscriptionContent from '@/components/inscription-content'
import Link from 'next/link'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import Loading from '@/components/circle-loading'
import { useRouter } from 'next/navigation'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import isFungible from '@/utils/is-fungible'
import CollectionIcon from '@/components/collection-icon'
import numberWithCommas from '@/utils/format-number'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import { LazyMotion, domAnimation, m } from 'framer-motion'
import VerifiedBadge from '@/components/verified-badge'
import { SnipeShield } from '@/components/secure-listing-badge'
import { isSnipeProtectedTrade } from '@/utils/trade-shield'
import { useSalesFeed } from '@/lib/sales-feed/hooks'
import getRelativeTime from '@/utils/get-relative-time'
import { EXPLORER_URL } from '@/utils/constants'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import { getSaleBoughtAt, getSalePriceSats, getSaleTxid } from '@/utils/sales-activity'

interface ActivityFeedProps {
	initialItems: any[]
	itemsCount: number
	isFullPage?: boolean
	hideHeader?: boolean
	activitySource?: 'local' | 'merged'
	scrollPageOnMount?: boolean
	/** True while the feed is hidden (a closed sidebar): no polling, rows stay as they are. */
	paused?: boolean
	/** Render a viewport's worth of rows and grow on scroll instead of every row (the home sidebar). */
	windowRows?: boolean
}


const _TEXT_COLLECTION_SLUGS = ['brc-20', 'tap', 'rune']

const saleTime = (item: any) => {
	if (typeof item?.ts === 'number') return item.ts
	const ts = Date.parse(getSaleBoughtAt(item))
	return Number.isFinite(ts) ? ts : 0
}

// The classified marketplace lives on the cross-marketplace sales feed
// (mirrors `marketplace` on GlobalSale / `/collection/{slug}/sales`). The
// global homepage feed exposes it on the item or its escrow once the API
// includes it; absent → no badge rather than a placeholder glyph.
const saleKey = (item: any) => {
	if (typeof item?.key === 'string') return item.key
	const escrow = item?.escrow || {}
	return [
		item?.id || item?.num || '',
		getSalePriceSats(item) || '',
		escrow?.seller_address || '',
		escrow?.buyer_address || '',
		escrow?.seller_address || escrow?.buyer_address ? '' : getSaleBoughtAt(item) || ''
	].join(':')
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
	initialItems,
	itemsCount: _itemsCount,
	isFullPage = false,
	hideHeader = false,
	activitySource: _activitySource = 'local',
	scrollPageOnMount = false,
	paused = false,
	windowRows = false
}): ReactElement => {
	const [visibleCount, setVisibleCount] = useState(30)
	const isCompactFeed = hideHeader && !isFullPage
	// The compact panel and the home sidebar render a viewport's worth of
	// rows and grow on scroll: the merged feed holds a few hundred rows, each
	// with a tooltip and artwork, and rendering them all on every refresh was
	// most of what the home page did while idle. The full /activity page
	// keeps every row so history paging works from one scroll.
	const isWindowed = isCompactFeed || windowRows
	useEffect(() => {
        if (!isWindowed) return
        const sizeForViewport = () => {
            const rowHeight = window.matchMedia('(min-width: 640px)').matches ? 27 : 44
            setVisibleCount(count => Math.max(count, Math.ceil((window.innerHeight - 180) / rowHeight) + 5))
        }
        const frame = requestAnimationFrame(sizeForViewport)
        window.addEventListener('resize', sizeForViewport)
        return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', sizeForViewport) }
    }, [isWindowed])
	const router = useRouter()
	const openInscriptionModal = useInscriptionModal()
	const [_copiedId, _setCopiedId] = useState<string | null>(null)
	const { exchangeRate } = useExchangeRate()
	const hoveredItemRef = useRef<string | null>(null)

	// One live tape from the API: every asset and venue, pending sales
	// included, merged and ordered on the server. The client renders it and
	// pages back with the server's cursor; it never merges or reorders.
	const feed = useSalesFeed(null, !paused)
	const items: any[] = feed.rows.length > 0 ? (feed.rows as any[]) : initialItems || []

	useEffect(() => {
		if (scrollPageOnMount) window.scrollTo(0, 0)
	}, [scrollPageOnMount])

	const recentItems = useFeedBlips(items.slice(0, 20).map(saleKey), items.slice(0, 20).map(saleTime))

	const visibleItems = isWindowed ? items.slice(0, visibleCount) : items

	const fetchMore = () => {
		if (isWindowed) {
			setVisibleCount((count) => count + 30)
			if (visibleCount < items.length) return
		}
		feed.fetchMore()
	}
	const hasMore = feed.hasMore || (isWindowed && visibleCount < items.length)
	const error = feed.error
	const isLoading = feed.isLoading && items.length === 0
	const retrying = feed.isLoadingMore
	const retry = () => feed.fetchMore()

	const renderItemContent = (item: any) => {
		if (item.alkane_trade) {
			const t = item.alkane_trade
			const fmt = (n: number | null | undefined) =>
				n == null
					? ''
					: n >= 1e6
					? `${(n / 1e6).toFixed(2)}M`
					: n >= 1e3
					? `${(n / 1e3).toFixed(2)}K`
					: n.toLocaleString('en-US', { maximumFractionDigits: n >= 1 ? 2 : 6 })
			const amount = fmt(t.amount)
			// One row per swap transaction, however many pools it hopped.
			const isSwap = t.side === 'swap' && t.paid_with
			return (
				<div className="flex min-w-0 items-center gap-x-1 w-full">
					<div className="flex items-center min-w-[1rem] min-h-[1rem] size-4">
						<CollectionIcon
							collection={item.collection}
							className="inline size-4 min-w-[1rem] min-h-[1rem] rounded-full"
							imageClassName="size-4 min-w-[1rem] min-h-[1rem] rounded-full"
						/>
					</div>
					<span className="min-w-0 text-xs truncate">
						{isSwap && (
							<span className="text-muted-foreground">
								<span className="tabular-nums">{fmt(t.paid_with.amount)}</span> {t.paid_with.asset_name} →{' '}
							</span>
						)}
						{amount && <span className="tabular-nums">{amount} </span>}
						{t.asset_name}
					</span>
					<span className={`inline-flex shrink-0 items-center text-[10px] font-medium ${isSwap ? 'text-purple-500 dark:text-purple-400' : t.side === 'buy' ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'}`}>
						{isSwap ? 'Swap' : t.side === 'buy' ? 'Buy' : 'Sell'}
					</span>
				</div>
			)
		}
		const salePriceSats = getSalePriceSats(item)
		const isItemFungible = isFungible(item?.collection?.slug)
		const jsonAmount = item?.meta?.attributes?.[0]?.value
		const name =
			item.meta?.name ||
			item.collection?.name
				.replace('TAP ', '')
				.replace('BRC20 ', '')
				.replace('DRC20 ', '')
				.replace('BEL20 ', '') ||
			item?.id?.slice(0, 6)

		const isImage = item?.content_type?.includes('image') || item?.collection?.slug === 'bitmap'
		const isText =
			!isImage && (item?.content_type?.includes('text') || item?.content_type?.includes('json'))

		return (
			<TooltipProvider delayDuration={0}>
				<Tooltip>
					<TooltipTrigger asChild>
						<div className="flex min-w-0 items-center gap-x-1 w-full">
							<div className="flex items-center min-w-[1rem] min-h-[1rem] size-4">
								{item?.collection?.slug === 'bitmap' ? (
									<svg viewBox="0 0 16 16" role="img" aria-label="Bitmap" className="size-4 rounded-sm" shapeRendering="crispEdges">
										<rect width="16" height="16" fill="#311b08" />
										<path d="M1 1h6v6H1zM8 1h3v3H8zM12 1h3v6h-3zM8 5h3v2H8zM1 8h3v7H1zM5 8h6v3H5zM12 8h3v7h-3zM5 12h2v3H5zM8 12h3v3H8z" fill="#f7931a" />
									</svg>
								) : isFungible(item?.collection?.slug) ? (
									<CollectionIcon
										collection={item.collection}
										className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
										imageClassName="size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									/>
								) : item?.collection?.slug && !isImage ? (
									<CollectionIcon
										collection={item.collection}
										className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
										imageClassName="size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									/>
								) : isText ? (
									<div className="inline-flex items-center justify-center mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm bg-chrome-muted overflow-hidden">
										<div className="size-16 scale-[0.25] origin-center">
											<InscriptionContent
												className="size-16"
												inscription={item}
												isDetail={false}
												showPricePer={false}
												smallCard={true}
											/>
										</div>
									</div>
								) : (
									<InscriptionContent
										className="inline mr-1 size-4 min-h-[1rem] min-w-[1rem] rounded-sm"
										inscription={item}
										isDetail={false}
										showPricePer={true}
										smallCard={true}
									/>
								)}
							</div>

							<span className="min-w-0 text-xs truncate">
								{isItemFungible && jsonAmount && `${numberWithCommas(jsonAmount)} `}
								{name}
								<VerifiedBadge slug={item?.collection?.slug} />
							</span>
						</div>
					</TooltipTrigger>
					<TooltipContent
						side="right"
						className="flex flex-col p-3 bg-popover border border-border rounded-md shadow-md"
					>
						<div className="text-xs text-muted-foreground mb-2">
							{item?.meta?.name || item.collection?.name || `Inscription #${item.num}`}
						</div>
						<div className="size-[160px] sm:size-[256px] bg-card rounded-sm overflow-hidden">
							{isRune(item?.collection?.slug) || isAlkanes(item?.collection?.slug) ? (
								<CollectionIcon
									collection={item.collection}
									className="inline mr-1 size-4 min-w-[1rem] min-h-[1rem] rounded-sm"
									imageClassName="h-full w-full min-w-[1rem] min-h-[1rem] rounded-sm"
								/>
							) : (
								<InscriptionContent
									className="size-full"
									inscription={item}
									isDetail={false}
									showPricePer={true}
									smallCard={true}
								/>
							)}
						</div>
						<div className="flex items-center justify-between mt-2 text-xs">
							<span className="text-muted-foreground">Sale</span>
							{salePriceSats > 0 && (
								<span className="font-medium flex items-center">
									<BitcoinIcon className="inline mr-1 size-3" />
									{item.estimate === 'min_out' ? '≥' : ''}{formatActivityBtc(salePriceSats)}
								</span>
							)}
						</div>
						<div className="flex items-center justify-between mt-1 text-xs">
							<div className="text-muted-foreground">
								{item.escrow?.seller_address && (
									<Link
										href={`/address/${item.escrow.seller_address}`}
										className="hover:text-primary"
										onClick={(e) => e.stopPropagation()}
									>
										{item.escrow.seller_address.slice(-5)}
									</Link>
								)}
								{item.escrow?.seller_address && item.escrow?.buyer_address && ' → '}
								{item.escrow?.buyer_address && (
									<Link
										href={`/address/${item.escrow.buyer_address}`}
										className="hover:text-primary"
										onClick={(e) => e.stopPropagation()}
									>
										{item.escrow.buyer_address.slice(-5)}
									</Link>
								)}
							</div>
							<span className="text-muted-foreground">
								${formatActivityUsd((salePriceSats / 1e8) * exchangeRate)}
							</span>
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		)
	}

	const handleRowClick = (item: any) => {
		const slug = item?.collection?.slug
		// Every sale with an inscription behind it (NFTs and BRC-20 / TAP
		// transfer inscriptions alike) opens the detail modal, which carries
		// the sale's settlement txid and live status. Runes and alkanes sales
		// have no inscription, so they still route to the collection page.
		if (item?.alkane_trade) {
			router.push(`/alkanes/${encodeURIComponent(item.alkane_trade.asset_id)}`)
		} else if (item?.id) {
			openInscriptionModal({ id: item.id, inscription: item })
		} else if (slug) {
			router.push(`/collection/${slug}`)
		}
	}

	return (
		<LazyMotion features={domAnimation}>
			<m.div
				initial={isFullPage || hideHeader ? false : { opacity: 0, x: 20 }}
				animate={{ opacity: 1, x: 0 }}
				transition={{ delay: 0.4, type: 'spring', stiffness: 50, damping: 20 }}
				className={
					isFullPage
						? 'w-full bg-transparent flex flex-col h-full'
						: 'fixed inset-y-0 right-0 w-[380px] bg-chrome-muted border-l border-border flex flex-col top-[55px]'
				}
			>
				{!hideHeader && (
					<div className="bg-transparent z-[1] px-4 py-3 border-b border-border">
						<h1 className="text-xs font-medium text-foreground -ml-1">Sales History</h1>
					</div>
				)}
				<div
					id="activity-scrollableDiv"
					className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
				>
					{isLoading && items.length === 0 && (
						<ActivityLoading />
					)}
					{error && (
						<div role="status" className="flex items-center justify-between gap-3 border-b border-border px-3 py-3 text-xs text-muted-foreground">
							<span>{items.length ? 'Updates delayed. Showing recent sales.' : 'Sales are taking longer to load.'}</span>
							<button type="button" onClick={retry} disabled={retrying} className="shrink-0 rounded border border-border px-3 py-2 text-foreground hover:bg-accent disabled:opacity-50">
								{retrying ? 'Retrying…' : 'Retry'}
							</button>
						</div>
					)}
					{!isLoading && !error && items.length === 0 && (
						<p className="px-4 py-8 text-center text-sm text-muted-foreground">No recent sales yet.</p>
					)}
					{items.length > 0 && (
						<InfiniteScroll
							dataLength={visibleItems.length}
							next={fetchMore}
							hasMore={hasMore || (isWindowed && visibleCount < items.length)}
							loader={feed.isLoadingMore ? (
								<div className="flex justify-center py-4">
									<Loading className="!w-auto !h-auto" />
								</div>
							) : null}
							scrollableTarget="activity-scrollableDiv"
							className="h-full"
							// The library makes its own box a touch scroller (overflow auto,
							// -webkit-overflow-scrolling touch). Nested inside the real
							// scroller, iOS Safari stops painting rows added after the
							// first screen, so the list scrolls into black.
							style={{ overflow: 'visible' }}
						>
							<div className="w-full">
								{visibleItems.map((item) => {
									const salePriceSats = getSalePriceSats(item)
									// The row's own time: sale time, or first seen in the mempool while pending.
									const boughtAt = typeof item.ts === 'number' ? new Date(item.ts).toISOString() : getSaleBoughtAt(item)
									const marketplace = saleMarketplace(item)
									const saleTxid = getSaleTxid(item)

									return (
										<div
											key={saleKey(item)}
											className="grid min-h-11 sm:min-h-0 grid-cols-[36px_minmax(0,1fr)_max-content] sm:grid-cols-[44px_minmax(0,1fr)_max-content] items-center whitespace-nowrap border-0 hover:bg-accent/50 cursor-pointer transition-all my-px"
                                            data-new-trade={recentItems.has(saleKey(item))}
												onClick={() => handleRowClick(item)}
												role="button"
												tabIndex={0}
												onKeyDown={(event) => {
													if (event.key === 'Enter' || event.key === ' ') {
														event.preventDefault()
														handleRowClick(item)
													}
												}}
												onMouseEnter={() => (hoveredItemRef.current = item.id)}
												onMouseLeave={() => (hoveredItemRef.current = null)}
											>
											<div suppressHydrationWarning className="py-1 pl-3 text-xs text-muted-foreground text-left">
												{saleTxid ? (
													<a
														href={`${EXPLORER_URL}/tx/${saleTxid}`}
														target="_blank"
														rel="noreferrer"
														title={`tx ${saleTxid}`}
														className="hover:underline hover:text-purple-700 dark:hover:text-purple-300"
														onClick={(e) => e.stopPropagation()}
														onKeyDown={(e) => e.stopPropagation()}
													>
														{getRelativeTime(boughtAt)}
													</a>
												) : (
													getRelativeTime(boughtAt)
												)}
											</div>
											<div className="min-w-0 p-1 text-xs text-left overflow-hidden">
												{renderItemContent(item)}
											</div>
											<div className="py-1 pr-3 sm:pr-4 pl-0 ml-3 text-xs text-right">
												<div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
													{isSnipeProtectedTrade(item) && (
														<SnipeShield
															className="mr-1 inline-flex shrink-0 text-emerald-400"
															iconClassName="size-3"
														/>
													)}
													{marketplace !== null && (
														<span className="mr-1 inline-flex shrink-0">
															<MarketplaceIcon marketplace={marketplace} />
														</span>
													)}
													{item.status === 'pending' && (
														<span className="shrink-0 inline-flex">
															<Loading size={10} className="!w-2.5 !h-2.5" />
														</span>
													)}
													<BitcoinIcon className="shrink-0 size-3 relative -top-[0.5px]" />
													<span className="tabular-nums whitespace-nowrap">
														{item.estimate === 'min_out' ? '≥' : ''}{formatActivityBtc(salePriceSats)}
														{Number.isFinite(exchangeRate) && exchangeRate > 0 && <span className="block sm:inline text-muted-foreground sm:ml-0.5 text-[10px]">
															($
															{formatActivityUsd((salePriceSats / 1e8) * exchangeRate)}
															)
														</span>}
													</span>
												</div>
											</div>
										</div>
									)
								})}
							</div>
						</InfiniteScroll>
					)}
				</div>
			</m.div>
		</LazyMotion>
	)
}

function _ClockIcon(props: React.SVGProps<SVGSVGElement>) {
	return (
		<svg
			{...props}
			xmlns="http://www.w3.org/2000/svg"
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<polyline points="12 6 12 12 16 14" />
		</svg>
	)
}

export default ActivityFeed
